import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import "../styles/Gateway.css";

import { getMe, getWorkspaceSettings } from "../api";
import {
  LEGACY_PLC_ROUTES,
  NAV_GROUPS,
  ROUTES,
  isNavItemAvailable,
  resolveGatewaySection,
} from "./gateway/navigation";
import {
  NAV_VISIBILITY_SETTING_KEY,
  isNavItemVisible,
  normalizeNavVisibility,
} from "./gateway/nav-visibility";
import { GatewayTopbar, PlaceholderPage } from "./gateway/shared";
import {
  AI_PROPOSAL_SECTION_ROUTES,
  aiProposalRouteIsActive,
} from "./gateway/ai-proposal-routes";
import chevronRightSrc from "../assets/chevron-right.svg";
import { useToasts } from "../hooks/useToasts";
import { ToastProvider } from "../ToastContext";
import { UserProvider } from "../UserContext";
import { useLicense } from "../LicenseContext";
import { useLicenseToast } from "../hooks/useLicenseToast";
import ToastStack from "../components/ToastStack";
import GatewayAIPopover from "../components/GatewayAIPopover";
import GatewayAIHighlight from "../components/GatewayAIHighlight";

const DEFAULT_SECTION_ID = "platform-overview";

export default function GatewayApp() {
  const { sectionId: sectionIdFromUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const license = useLicense();
  const { licensingEnabled, products, loading } = license;
  const routeResolution = useMemo(() => (
    sectionIdFromUrl
      ? resolveGatewaySection(sectionIdFromUrl, location.search, products)
      : { id: DEFAULT_SECTION_ID, search: location.search, redirected: false }
  ), [sectionIdFromUrl, location.search, products]);
  const active = routeResolution?.id || DEFAULT_SECTION_ID;
  // Unknown section in URL — redirect to canonical default so the URL
  // matches what's actually rendered (and the active-nav highlight stays
  // consistent with the address bar).
  useEffect(() => {
    if (sectionIdFromUrl && !routeResolution) {
      navigate("/", { replace: true });
      return;
    }
    if (sectionIdFromUrl && routeResolution?.redirected) {
      navigate(`/${routeResolution.id}${routeResolution.search || ""}`, { replace: true });
    }
  }, [sectionIdFromUrl, navigate, routeResolution]);
  const setActive = useCallback(
    (next, search = "") => {
      const resolved = next ? resolveGatewaySection(next, search, products) : null;
      const target = resolved?.id || DEFAULT_SECTION_ID;
      const path = target === DEFAULT_SECTION_ID
        ? "/"
        : `/${target}${resolved?.search || ""}`;
      navigate(path);
    },
    [navigate, products]
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  // Which sidebar sections this workspace shows. Starts empty so the built-in
  // defaults apply while the fetch is in flight — the curated nav renders
  // immediately instead of flashing the full section list.
  const [navVisibility, setNavVisibility] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [navOpen, setNavOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(() => {
    try {
      return window.localStorage.getItem("gw-nav-hidden") === "1";
    } catch {
      return false;
    }
  });
  const [aiOpen, setAIOpen] = useState(false);
  // Inbound prompts from other parts of the gateway (e.g. the Trends
  // Journal's "Ask AI" button) flow through this transient state.
  // Cleared after the popover consumes it on each open.
  const [aiInitialPrompt, setAIInitialPrompt] = useState(null);
  const [aiProposalTick, setAIProposalTick] = useState(0);
  useEffect(() => {
    const handler = (ev) => {
      const next = ev?.detail?.prompt;
      // The prompt is optional: the Custom connectors editor raises the drawer
      // with nothing typed, because what to scrape is the operator's sentence
      // to write, not ours.
      if (typeof next === "string" && next) setAIInitialPrompt(next);
      setAIOpen(true);
    };
    window.addEventListener("gw-open-ai-with-prompt", handler);
    return () => window.removeEventListener("gw-open-ai-with-prompt", handler);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem("gw-nav-hidden", navHidden ? "1" : "0");
    } catch {}
  }, [navHidden]);
  const { toasts, notify, dismissToast, pauseToast, resumeToast } = useToasts();
  // Fires a persistent toast on app mount when license isn't healthy
  // and auto-dismisses it once the LicenseProvider's poll picks up a
  // healthy state (admin uploaded a renewal, etc.). Replaces the old
  // top-bar banner.
  useLicenseToast({ license, notify, dismissToast, onNavigate: setActive });

  const toggleGroup = useCallback((title) => {
    setCollapsed((prev) => ({ ...prev, [title]: !prev[title] }));
  }, []);

  const refreshUser = useCallback(() => {
    getMe()
      .then((me) => {
        setIsAdmin((me.permissions || []).includes("admin"));
        setCurrentUser(me);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  /* Listen for profile updates from account page */
  useEffect(() => {
    const handler = () => refreshUser();
    window.addEventListener("gw-profile-updated", handler);
    return () => window.removeEventListener("gw-profile-updated", handler);
  }, [refreshUser]);

  /* Sidebar visibility, saved workspace-wide from Gateway Settings. Reloaded
     when that page saves and when the AI edits workspace settings, so the nav
     reflects the change without a refresh. A failed fetch leaves the defaults
     in place rather than emptying the sidebar. */
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getWorkspaceSettings()
        .then((res) => {
          if (cancelled) return;
          setNavVisibility(
            normalizeNavVisibility(res?.settings?.[NAV_VISIBILITY_SETTING_KEY])
          );
        })
        .catch(() => {});
    };
    load();
    const onAIChange = (e) => {
      if (e.detail?.resource === "workspace_settings") load();
    };
    window.addEventListener("gw-nav-visibility-changed", load);
    window.addEventListener("gw-ai-resource-changed", onAIChange);
    return () => {
      cancelled = true;
      window.removeEventListener("gw-nav-visibility-changed", load);
      window.removeEventListener("gw-ai-resource-changed", onAIChange);
    };
  }, []);

  // Pending propose payload + a tick counter. The AI propose-* tools dispatch
  // a CustomEvent on `window`. We capture the payload here, navigate to the
  // matching section, then forward the payload to that section page via a
  // second event scoped to the page-side listener — by then the page is
  // mounted and listening. The tick counter ensures repeated proposals fire
  // a fresh event even if the section was already active.
  const aiProposeRef = useRef({ event: null, payload: null, tick: 0 });
  useEffect(() => {
    const handler = (e) => {
      const eventName = e.type;
      const routeValue = AI_PROPOSAL_SECTION_ROUTES[eventName];
      if (!routeValue) return;
      const route = typeof routeValue === "string" ? { id: routeValue } : routeValue;
      const payload = e.detail?.payload || {};
      const tick = aiProposeRef.current.tick + 1;
      aiProposeRef.current = {
        event: eventName,
        payload,
        tick,
        route,
      };
      if (!aiProposalRouteIsActive(route, active, location.search)) {
        setActive(route.id, route.search || "");
      }
      setAIProposalTick(tick);
    };
    const events = Object.keys(AI_PROPOSAL_SECTION_ROUTES);
    events.forEach((evt) => window.addEventListener(evt, handler));
    return () => events.forEach((evt) => window.removeEventListener(evt, handler));
  }, [active, location.search, setActive]);

  // Dispatch exactly once after the owning canonical route and, for Runtime
  // Fleet, the correct tab have mounted. This avoids duplicate proposal
  // events and keeps the inactive tab unmounted (therefore not polling).
  useEffect(() => {
    const pending = aiProposeRef.current;
    if (!pending?.event || pending.tick !== aiProposalTick || !pending.route) return undefined;
    if (!aiProposalRouteIsActive(pending.route, active, location.search)) return undefined;
    const timer = window.setTimeout(() => {
      if (aiProposeRef.current.tick !== pending.tick) return;
      try {
        window.dispatchEvent(new CustomEvent(`${pending.event}-page`, {
          detail: { payload: pending.payload, tick: pending.tick },
        }));
      } catch {}
      if (aiProposeRef.current.tick === pending.tick) {
        aiProposeRef.current = { event: null, payload: null, tick: pending.tick };
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [active, aiProposalTick, location.search]);

  const aiCtx = {
    activeSection: active,
    user: currentUser,
    license: {
      licensingEnabled: license.licensingEnabled,
      mode: license.mode,
      daysRemaining: license.daysRemaining,
      expiresAt: license.expiresAt,
      customer: license.customer,
      edition: license.edition,
    },
    navigate: (sectionId, options = {}) => {
      // Reject unknown ids upfront so navigate_to can surface a clear error
      // back to the model — otherwise setActive silently falls back to the
      // default section and the AI thinks it succeeded. A section the
      // current SKU isn't entitled to is rejected the same way (it won't
      // resolve for this user, so it's effectively unknown to them).
      if (!sectionId || !resolveGatewaySection(sectionId, "", products)) {
        const valid = [...Object.keys(ROUTES), ...Object.keys(LEGACY_PLC_ROUTES)];
        const err = new Error(
          `Unknown sectionId "${sectionId}". Valid ids: ${valid.join(", ")}`
        );
        err.code = "UNKNOWN_SECTION";
        throw err;
      }
      const resolved = resolveGatewaySection(sectionId, "", products);
      const view = String(options?.view || "").trim();
      if (view && (resolved?.id !== "plc-runtime-fleet" || !["hosts", "controllers"].includes(view))) {
        const err = new Error(`The view option is valid only for plc-runtime-fleet and must be hosts or controllers.`);
        err.code = "UNKNOWN_SECTION_VIEW";
        throw err;
      }
      setActive(sectionId, view ? `?view=${encodeURIComponent(view)}` : "");
    },
  };

  const activeRoute = ROUTES[active];
  const ActiveComponent = activeRoute?.component ?? PlaceholderPage;
  const activeProps =
    activeRoute?.props ??
    (activeRoute
      ? {}
      : {
          title: "Gateway",
          subtitle: "Select a section to get started.",
        });
  const content = (
    <ActiveComponent
      {...activeProps}
      routeSearch={routeResolution?.search ?? location.search}
    />
  );

  return (
    <ToastProvider value={notify}>
    <UserProvider value={currentUser}>
    <div className={`gwShell ${aiOpen ? "gwShellAIOpen" : ""}`}>
      <GatewayTopbar
        user={currentUser}
        onNavigate={setActive}
        onMenuToggle={() => setNavOpen((o) => !o)}
        aiOpen={aiOpen}
        onToggleAI={() => setAIOpen((o) => !o)}
      />

      {/* Mobile nav overlay */}
      {navOpen && <div className="gwNavOverlay" onClick={() => setNavOpen(false)} />}

      <div className={`gwBody ${navHidden ? "gwBodyNavHidden" : ""}`}>
        {navHidden && (
          <button
            type="button"
            className="gwNavExpandHandle"
            onClick={() => setNavHidden(false)}
            aria-label="Show navigation"
            title="Show navigation"
          >
            <img src={chevronRightSrc} alt="" className="gwNavExpandChevron" />
          </button>
        )}
        <aside className={`gwNav ${navOpen ? "gwNavOpen" : ""} ${navHidden ? "gwNavHidden" : ""}`}>
          <button
            type="button"
            className="gwNavCollapseBtn"
            onClick={() => setNavHidden(true)}
            aria-label="Hide navigation"
            title="Hide navigation"
          >
            <img src={chevronRightSrc} alt="" className="gwNavCollapseChevron" />
          </button>
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(
              (item) =>
                isNavItemAvailable(item, {
                  isAdmin,
                  licensingEnabled,
                  // While the license is still loading, entitlements are unknown —
                  // hide SKU-gated items (empty set) rather than assuming both SKUs,
                  // so a single-SKU gateway doesn't flash the other SKU's tabs.
                  products: loading ? [] : products,
                }) && isNavItemVisible(navVisibility, item.id)
            );
            if (visibleItems.length === 0) return null;
            return (
              <div className="gwNavGroup" key={group.title}>
                <button
                  className={`gwNavTitle ${collapsed[group.title] ? "isCollapsed" : ""}`}
                  type="button"
                  onClick={() => toggleGroup(group.title)}
                >
                  <span>{group.title}</span>
                  <img src={chevronRightSrc} alt="" className="gwNavChevron" />
                </button>
                {!collapsed[group.title] &&
                  visibleItems.map((item) => (
                    <button
                      className={`gwNavItem ${active === item.id ? "isActive" : ""} ${
                        item.tone === "deferred" ? "isDeferred" : ""
                      }`}
                      key={item.id}
                      type="button"
                      data-gw-nav-item={item.id}
                      onClick={() => { setActive(item.id); setNavOpen(false); }}
                    >
                      <span>{item.label}</span>
                      {item.tone === "deferred" && (
                        <span className="gwNavBadge">Later</span>
                      )}
                    </button>
                  ))}
              </div>
            );
          })}
        </aside>

        <main className="gwMain" data-gw-section={active}>{content}</main>
      </div>
    </div>
    <ToastStack toasts={toasts} onDismiss={dismissToast} onPause={pauseToast} onResume={resumeToast} />
    <GatewayAIPopover
      open={aiOpen}
      onClose={() => setAIOpen(false)}
      ctx={aiCtx}
      initialPrompt={aiInitialPrompt}
      onConsumedInitialPrompt={() => setAIInitialPrompt(null)}
    />
    <GatewayAIHighlight />
    </UserProvider>
    </ToastProvider>
  );
}
